import oikosApi from '../../../services/oikos';
import qs from 'qs';
import { getContrFornDocumentosObrigatoriosIds } from './contrFornExtendedFields';

const normalizeEmail = (value) => {
  const email = String(value || '').trim();
  return email || null;
};

export async function fetchContratadoEmail(parteTab, parteId) {
  if (!parteTab || !parteId) return null;

  const table = parteTab === 'pf' ? 'pf' : parteTab === 'pj' ? 'pj' : null;
  if (!table) return null;

  const idField = table === 'pf' ? 'pf_id' : 'pj_id';
  const emailField = table === 'pf' ? 'pf_emailProf' : 'pj_emailPrin';
  const nomeField = table === 'pf' ? 'pf_nomeExibicao' : 'pj_razaosocial';

  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table,
      data: { [idField]: parteId },
    })}`);

    const row = res?.data?.[0];
    const email = normalizeEmail(row?.[emailField]);

    if (!email) return null;

    return {
      email,
      nome: String(row?.[nomeField] || '').trim() || null,
      identificador: null,
      source: `contratado_${parteTab}`,
    };
  } catch (e) {
    console.log('Erro ao buscar e-mail do contratado:', e);
    return null;
  }
}

/** Retorna o registro completo de pf/pj do Contratado selecionado (ou null). */
export async function fetchContratadoRecord(parteTab, parteId) {
  const table = String(parteTab || '').trim().toLowerCase();
  const id = Number(parteId) || 0;
  if ((table !== 'pf' && table !== 'pj') || !id) return null;

  const idField = table === 'pf' ? 'pf_id' : 'pj_id';

  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table,
      data: { [idField]: id },
    })}`);

    return res?.data?.[0] || null;
  } catch (e) {
    console.log('Erro ao buscar registro do contratado:', e);
    return null;
  }
}

function resolveFromFornecedorSelect(parteTab, parteId, selPfsFornecedor, selPjsFornecedor) {
  if (parteTab === 'pf' && parteId) {
    const pf = selPfsFornecedor.find((item) => Number(item.value) === Number(parteId));
    const email = normalizeEmail(pf?.pf_emailProf);
    if (email) {
      return {
        email,
        nome: pf?.label?.trim() || null,
        identificador: null,
        source: 'contratado_pf_select',
      };
    }
  }

  if (parteTab === 'pj' && parteId) {
    const pj = selPjsFornecedor.find((item) => Number(item.value) === Number(parteId));
    const email = normalizeEmail(pj?.pj_emailPrin);
    if (email) {
      return {
        email,
        nome: pj?.label?.trim() || null,
        identificador: null,
        source: 'contratado_pj_select',
      };
    }
  }

  return null;
}

export async function resolveContratacaoEmailDestino({
  compraCotacaoData = {},
  contrFornRecord = {},
  selPfsFornecedor = [],
  selPjsFornecedor = [],
}) {
  const parteTab = contrFornRecord.contrForn_parteTab
    || compraCotacaoData.compraCotacao_parteTab
    || null;
  const parteId = contrFornRecord.contrForn_parteId
    || compraCotacaoData.compraCotacao_parteId
    || null;

  const fromContratado = await fetchContratadoEmail(parteTab, parteId);
  if (fromContratado) return fromContratado;

  const fromSelect = resolveFromFornecedorSelect(
    parteTab,
    parteId,
    selPfsFornecedor,
    selPjsFornecedor,
  );
  if (fromSelect) return fromSelect;

  const fromCandidato = normalizeEmail(compraCotacaoData.candidato_email);
  if (fromCandidato) {
    return {
      email: fromCandidato,
      nome: compraCotacaoData.candidato_nome?.trim() || null,
      identificador: compraCotacaoData.candidato_cpf || compraCotacaoData.candidato_identificador || null,
      source: 'candidato',
    };
  }

  // Não usar compraCotacao_emailInteressado: o destino da Contratação é o
  // e-mail profissional/principal do Contratado (pf_emailProf / pj_emailPrin).
  return null;
}

export async function fetchCompraCotacaoById(compraCotacaoId) {
  const id = Number(compraCotacaoId) || 0;
  if (!id) return null;

  try {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'compraCotacao',
      data: { compraCotacao_id: id },
    })}`);

    return res?.data?.[0] || null;
  } catch (e) {
    console.log('Erro ao buscar compraCotacao:', e);
    return null;
  }
}

export async function resolveCompraCotacaoDataForHash(cotacaoSource = {}, contrFornRecord = {}) {
  const compraCotacaoId = Number(
    cotacaoSource?.compraCotacao_id
    || contrFornRecord?.compraCotacao_id,
  ) || 0;

  if (!compraCotacaoId) {
    return cotacaoSource?.candidato_id ? null : cotacaoSource;
  }

  const fetched = await fetchCompraCotacaoById(compraCotacaoId);
  if (!fetched) return cotacaoSource;

  return {
    ...fetched,
    orcItem_id: fetched.orcItem_id || cotacaoSource?.orcItem_id || null,
  };
}

export function mergeCandidatoContext(compraCotacaoRecord = {}, candidatoContext = {}) {
  if (!candidatoContext?.candidato_id) return compraCotacaoRecord;

  return {
    ...compraCotacaoRecord,
    candidato_id: candidatoContext.candidato_id,
    candidato_email: candidatoContext.candidato_email,
    candidato_nome: candidatoContext.candidato_nome,
    candidato_cpf: candidatoContext.candidato_cpf,
    candidato_identificador: candidatoContext.candidato_identificador,
    candidato_valorSalario: candidatoContext.candidato_valorSalario,
  };
}

export async function fetchContrFornRecordForEmail({
  contrFornRecord = {},
  contrFornId = null,
  compraCotacaoId = null,
} = {}) {
  let resolved = { ...contrFornRecord };
  const normalizedContrFornId = Number(contrFornId || contrFornRecord?.contrForn_id) || 0;
  const normalizedCompraCotacaoId = Number(
    compraCotacaoId || contrFornRecord?.compraCotacao_id,
  ) || 0;

  const applyRow = (fullData) => {
    if (!fullData) return resolved;
    return {
      ...resolved,
      ...fullData,
      contrForn_documentosObrigatorios: getContrFornDocumentosObrigatoriosIds(fullData),
    };
  };

  try {
    if (normalizedContrFornId) {
      const res = await oikosApi.get(`/?${qs.stringify({
        action: 'get',
        table: 'contrForn',
        data: { contrForn_id: normalizedContrFornId },
      })}`);
      return applyRow(res?.data?.[0]);
    }

    if (normalizedCompraCotacaoId) {
      const res = await oikosApi.get(`/?${qs.stringify({
        action: 'get',
        table: 'contrForn',
        data: { compraCotacao_id: normalizedCompraCotacaoId },
        order: 'contrForn_id DESC',
      })}`);
      return applyRow(res?.data?.[0]);
    }
  } catch (error) {
    console.error('Erro ao buscar contrato para e-mail:', error);
  }

  return resolved;
}

export function buildContratacaoHashFiles(pdfDocument, formDocuments = []) {
  const files = [];

  if (pdfDocument?.link) {
    files.push({
      link: pdfDocument.link,
      desc: pdfDocument.documento_desc || 'Resumo da contratação em PDF',
    });
  }

  (formDocuments || []).forEach((documento) => {
    if (!documento?.link) return;

    files.push({
      link: documento.link,
      desc: documento.documento_desc || documento.arquivo_caminho || 'Anexo do contrato',
    });
  });

  return files;
}

export function getAnexosParaPdf(savedAnexos = null, filesContrForn = []) {
  if (Array.isArray(savedAnexos) && savedAnexos.length > 0) {
    return savedAnexos.filter((anexo) => anexo?.link || anexo?.documento_desc || anexo?.arquivo_caminho);
  }

  return (filesContrForn || [])
    .filter((file) => file?.data)
    .map((file) => ({
      documento_desc: file.props?.description || file.data?.path || 'Anexo do contrato',
      link: null,
    }));
}
